import { motion } from "framer-motion";
import { ChevronDown, Crown, Sparkles, Skull, Check, Info } from "lucide-react";
import { nationalFlag } from "@/lib/flags";
import ChipStatusPill from "@/components/onui/ChipStatusPill";
import { TOURNAMENT_COMPLETE_LOCK_COPY } from "@/lib/todoStatus";
import { BentoModal } from "@/components/onui/BentoMotion";

/**
 * KnockoutPickModal — Champion Bonus picker.
 *
 * iter87.21 — Split into two sections so the user can always see their
 * ACTUAL current pick, even after that team was eliminated:
 *   1. "Your pick" — surfaces the user's current champion-bonus pick
 *      alongside its status (Champion / Eliminated / Still Alive).
 *      Rendered whether the team is eliminated or not.
 *   2. "Change to" — grid of remaining eligible teams to switch to.
 *      Only rendered when picking is still open (status !== "locked").
 *
 * Previously the modal received only `availableTeams` (non-eliminated),
 * which meant that once your pick was knocked out you saw ONLY the
 * eventual champion in the grid — misleading (looked like the modal
 * was claiming that team was YOUR pick).
 */
export default function KnockoutPickModal({
    open = true,
    teams,                  // switchable (non-eliminated) teams
    currentPickTeam,        // full team object of the user's current pick (may be eliminated)
    currentPickTeamId,
    status,
    onPick,
    onClose,
    // iter87.34 — Per-template copy. When rendered for Season Pot
    // Pick'em (NFL / Super Draft), we swap the title, tagline, and
    // "won it all" copy so the modal reads correctly for a season-
    // long "who finishes on top" bet vs. a knockout-tournament
    // "who lifts the trophy" bet.
    template,
    isPotPickem = false,
    championBonus = 0,
    tournamentComplete = false,
}) {
    const isLocked = status === "locked";
    const pickEliminated = !!currentPickTeam?.eliminated;
    const pickWasChampion = currentPickTeam?.stage_reached === "champion";
    const otherTeams = (teams || []).filter(t => t.id !== currentPickTeamId);

    // iter87.34 — Template-aware copy.
    // iter87.199.37 — SuperDraft retired; only NFL Season Pot uses the
    // Champions Pick season copy today.
    const isNflSeasonPot = template === "nfl_season_api" && isPotPickem;
    const isSeasonPot = isNflSeasonPot;
    const title = isSeasonPot ? "Champions Pick" : "Champion Bonus";
    const bonusTag = championBonus ? ` · +${championBonus} pts` : "";
    const promptCopy = isNflSeasonPot
        ? `Nominate the team you think finishes with the BEST RECORD in the NFL. Nail it and bank +${championBonus} when the season wraps — record ties pay every correct picker. Any team counts.`
        : "Points decay as more rounds start. Change anytime before the final kicks off.";
    const wonCopy = isSeasonPot ? "Finished on top — bonus banked" : "Won it all — bonus banked";
    const eliminatedCopy = isSeasonPot
        ? "Didn't post the best record"
        : "Eliminated — didn't win";
    const stillAliveCopy = isSeasonPot ? "Season still running" : "Still alive";
    const pickHeadingCopy = currentPickTeam
        ? (isSeasonPot ? "Change your Champions Pick" : "Change to")
        : (isSeasonPot ? "Pick your champion" : "Pick a champion");
    // iter87.43 — Locked-Chip Copy Sweep. When the whole tournament is
    // done, collapse the four "why locked" variants below to a single
    // canonical string so every pick surface reads consistently on a
    // COMPLETE / FINAL tournament (previously said "the final has
    // kicked off" / "the season has started" — technically true but
    // confusing days or weeks after the tournament ended).
    const lockedCopy = tournamentComplete
        ? TOURNAMENT_COMPLETE_LOCK_COPY
        : isSeasonPot
            ? (currentPickTeam
                ? "Champions Pick is locked — the season has started."
                : "Champions Pick is locked — no bonus will be awarded.")
            : (currentPickTeam
                ? "Champion picks are locked for this tournament — the final has kicked off."
                : "Champion picks are locked for this tournament — no bonus will be awarded.");

    return (
        <BentoModal
            open={open}
            onClose={onClose}
            testId="knockout-pick-modal"
            backdropClassName="lg:items-center lg:justify-center lg:p-4"
            panelClassName="rounded-2xl border border-cyber/40 bg-obsidian p-4 sm:p-6 max-w-lg w-full max-h-[90dvh] overflow-y-auto"
        >
                <div className="flex items-center justify-between mb-3 gap-2">
                    <div className="min-w-0 flex items-center gap-2 flex-1">
                        <div className="w-9 h-9 rounded-xl bg-cyber flex items-center justify-center shrink-0">
                            <Crown className="w-4 h-4 text-black" strokeWidth={2.5} />
                        </div>
                        <h3 className="font-display font-extrabold text-lg uppercase truncate">{title}{bonusTag}</h3>
                        {status && <ChipStatusPill status={status} testId="knockout-pick-modal-status" />}
                    </div>
                    <button
                        onClick={onClose}
                        className="w-10 h-10 rounded-full bg-white/[0.06] hover:bg-white/[0.12] flex items-center justify-center transition-colors active:scale-95 shrink-0"
                        data-testid="close-bonus-modal-btn"
                        aria-label="Close"
                    >
                        <ChevronDown className="w-5 h-5" />
                    </button>
                </div>

                {/* Section 1 — Your current pick */}
                <div className="mb-4" data-testid="knockout-pick-current-section">
                    <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-2 flex items-center gap-1.5">
                        <Sparkles className="w-3 h-3 text-cyber" /> Your pick
                    </p>
                    {currentPickTeam ? (
                        <div
                            data-testid={`knockout-pick-current-${currentPickTeam.id}`}
                            className={`rounded-lg border p-3 flex items-center gap-3 ${
                                pickWasChampion
                                    ? "border-cyber/60 bg-cyber/[0.08] shadow-[0_0_20px_-8px_rgba(212,246,68,0.45)]"
                                    : pickEliminated
                                        ? "border-heat/50 bg-heat/[0.06]"
                                        : "border-cyber/40 bg-cyber/[0.05]"
                            }`}
                        >
                            <div className="w-10 h-10 rounded-full border border-white/10 bg-white/[0.04] flex items-center justify-center text-xl shrink-0">
                                {nationalFlag(currentPickTeam.flag) || "🏳️"}
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="font-bold text-base truncate">{currentPickTeam.name}</p>
                                {pickWasChampion ? (
                                    <p className="text-[10px] uppercase tracking-widest text-cyber font-bold flex items-center gap-1 mt-0.5">
                                        <Check className="w-3 h-3" /> {wonCopy}
                                    </p>
                                ) : pickEliminated ? (
                                    <p className="text-[10px] uppercase tracking-widest text-heat font-bold flex items-center gap-1 mt-0.5">
                                        <Skull className="w-3 h-3" /> {eliminatedCopy}
                                    </p>
                                ) : (
                                    <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mt-0.5">
                                        {stillAliveCopy}
                                    </p>
                                )}
                            </div>
                        </div>
                    ) : (
                        <p className="text-xs text-zinc-500 rounded-lg border border-white/[0.08] bg-white/[0.02] p-3">
                            You haven't picked a champion yet.
                        </p>
                    )}
                </div>

                {/* Section 2 — Switchable options (only while picking is open) */}
                {!isLocked && otherTeams.length > 0 && (
                    <div data-testid="knockout-pick-switch-section">
                        <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-2">
                            {pickHeadingCopy}
                        </p>
                        <div className="text-xs text-zinc-500 mb-3 flex gap-2 items-start text-left">
                            <Info className="w-4 h-4 text-cyber shrink-0 mt-0.5" />
                            <p className="leading-relaxed">
                                <span className="text-white font-bold uppercase tracking-tight">How it works:</span>{" "}
                                {promptCopy}
                            </p>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                            {otherTeams.map((t) => (
                                <motion.button
                                    key={t.id}
                                    data-testid={`bonus-team-${t.id}`}
                                    onClick={() => onPick(t.id)}
                                    whileHover={{ y: -2, scale: 1.015 }}
                                    whileTap={{ scale: 0.975 }}
                                    transition={{ type: "spring", stiffness: 400, damping: 30 }}
                                    className="p-3 rounded-lg border border-white/[0.08] bg-[#141414] hover:bg-[#181818] hover:border-cyber/40 text-left"
                                >
                                    {nationalFlag(t.flag) && <div className="text-xl mb-1">{nationalFlag(t.flag)}</div>}
                                    <div className="text-sm font-bold truncate">{t.name}</div>
                                </motion.button>
                            ))}
                        </div>
                    </div>
                )}

                {isLocked && (
                    <p className="text-[11px] text-zinc-500 mt-2" data-testid="knockout-pick-locked-msg">
                        {lockedCopy}
                    </p>
                )}
        </BentoModal>
    );
}
